import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useCreateSablona } from "@/api/hooks/useSablony";

/**
 * Uloží zostavu (aj s dielcami, hranami a kovaním) ako globálnu
 * šablónu — vložiteľnú do ľubovoľnej zákazky cez „Vložiť šablónu".
 */
export function SablonaSaveDialog({
  zostavaId,
  zostavaNazov,
  open,
  onOpenChange,
}: {
  zostavaId: string;
  zostavaNazov: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const create = useCreateSablona();
  const [nazov, setNazov] = useState(zostavaNazov);
  const [popis, setPopis] = useState("");
  useEffect(() => {
    if (open) {
      setNazov(zostavaNazov);
      setPopis("");
    }
  }, [open, zostavaNazov]);

  async function handleSave(): Promise<void> {
    try {
      await create.mutateAsync({
        nazov: nazov.trim(),
        popis: popis.trim() || null,
        typ: "zostava",
        zostava_id: zostavaId,
      });
      toast({
        title: "Šablóna uložená",
        description: `„${nazov.trim()}" — vložíš ju cez tlačidlo Šablóna v ľubovoľnej zákazke.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Šablónu sa nepodarilo uložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Uložiť ako šablónu</DialogTitle>
          <DialogDescription className="sr-only">
            Uloženie zostavy ako šablóny na opakované použitie.
          </DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Šablóna uloží zostavu aj s dielcami, hranami a kovaním.
          Materiály (M1, M2…) sa pri vložení prispôsobia
          materiálom cieľovej zákazky.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sab-nazov" className="text-xs">
              Názov šablóny
            </Label>
            <Input
              id="sab-nazov"
              value={nazov}
              onChange={(e) => setNazov(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sab-popis" className="text-xs">
              Popis (voliteľný)
            </Label>
            <Input
              id="sab-popis"
              value={popis}
              onChange={(e) => setPopis(e.target.value)}
              placeholder="napr. spodná skrinka 600 s policou"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            title="Zavrieť bez uloženia"
          >
            Zrušiť
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={!nazov.trim() || create.isPending}
            title="Uloží zostavu ako globálnu šablónu"
          >
            {create.isPending ? "Ukladám…" : "Uložiť šablónu"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
